import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SelectOption } from "@outpave/ds";
import { DEBOUNCE_TIMEOUT } from "../components/common/Constants";

/** One page of results from a cursor-paginated backend search. */
export interface SelectSearchPage<T extends SelectOption = SelectOption> {
  options: T[];
  hasNextPage?: boolean;
  endCursor?: string | null;
}

export interface UseSelectSearchArgs<T extends SelectOption> {
  /** Fetch one page. `after` is null for the first page of a query. */
  fetchPage: (
    query: string,
    after: string | null,
  ) => Promise<SelectSearchPage<T>>;
  /**
   * The currently-saved option. The DS resolves a value's label only by finding
   * its id in `options`, so without this a saved value renders as an empty
   * field until the user happens to search it up. Memoize it.
   */
  seed?: T | null;
  debounceMs?: number;
  skip?: boolean;
  /**
   * Re-runs the search from the first page when it changes. `fetchPage` is
   * held in a ref and deliberately does not, so a filter the app owns outside
   * the query text (an allocation type, an account) has no other way to
   * invalidate the loaded pages.
   */
  refetchKey?: string;
}

export interface SelectSearch<T extends SelectOption> {
  options: T[];
  loading: boolean;
  onInputChange: (query: string) => void;
  onScrollEnd: () => void;
  /** Any option this hook has ever loaded, for reading a selection back. */
  optionById: (id: string) => T | undefined;
  /**
   * The term the CURRENT `options` were fetched with — for `Select.highlight`.
   * Deliberately not the live input: marking against what the user is still
   * typing makes the bold run ahead of the rows it is meant to explain.
   */
  query: string;
  /**
   * Drop the query and refetch the first page. The DS's clear button only
   * fires `onChange`, so without this a cleared field keeps the list filtered
   * by whatever was last typed.
   */
  reset: () => void;
}

/**
 * Backend search and cursor pagination for a DS `Select`, which is pure
 * presentation and never fetches: it reports `onInputChange` / `onScrollEnd`
 * and renders whatever `options` and `loading` the app hands back.
 */
export function useSelectSearch<T extends SelectOption>({
  fetchPage,
  seed = null,
  debounceMs = DEBOUNCE_TIMEOUT,
  skip = false,
  refetchKey,
}: UseSelectSearchArgs<T>): SelectSearch<T> {
  const [query, setQuery] = useState("");
  const [pageOptions, setPageOptions] = useState<T[]>([]);
  // Lags `query` by one resolved fetch on purpose; see `SelectSearch.query`.
  const [resultQuery, setResultQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Call sites routinely build this inline, so depending on its identity would
  // refetch on every parent render — including the one selecting an option
  // causes. Same trap the deleted ds/Select wrapper documented.
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const seedRef = useRef(seed);
  seedRef.current = seed;

  const seenRef = useRef(new Map<string, T>());
  const pageRef = useRef<{ hasNextPage: boolean; endCursor: string | null }>({
    hasNextPage: false,
    endCursor: null,
  });
  // Monotonic: a resolution from a superseded query is dropped rather than
  // overwriting fresher results.
  const requestRef = useRef(0);
  const appendingRef = useRef(false);

  const remember = useCallback((opts: T[]) => {
    opts.forEach((o) => seenRef.current.set(o.id, o));
  }, []);

  useEffect(() => {
    // Bump first, and unconditionally: a new query (or a flip to `skip`)
    // invalidates the one before it, and its cursor along with it — paging must
    // not carry a stale `endCursor` into a different result set.
    const requestId = ++requestRef.current;
    pageRef.current = { hasNextPage: false, endCursor: null };
    appendingRef.current = false;
    if (skip) {
      setLoading(false);
      return undefined;
    }

    const run = async () => {
      setLoading(true);
      try {
        const page = await fetchPageRef.current(query, null);
        if (requestRef.current !== requestId) return;
        remember(page.options);
        pageRef.current = {
          hasNextPage: Boolean(page.hasNextPage),
          endCursor: page.endCursor ?? null,
        };
        setPageOptions(page.options);
        setResultQuery(query);
      } catch (error) {
        if (requestRef.current === requestId) {
          console.error("Select search failed:", error);
        }
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    };

    // The empty query runs immediately so opening an untouched field still
    // shows a list, which is what the old wrapper's eager mount fetch did.
    const timer = setTimeout(run, query ? debounceMs : 0);
    return () => clearTimeout(timer);
  }, [query, debounceMs, skip, remember, refetchKey]);

  const onScrollEnd = useCallback(() => {
    const { hasNextPage, endCursor } = pageRef.current;
    if (skip || !hasNextPage || !endCursor || appendingRef.current) return;
    appendingRef.current = true;
    const requestId = requestRef.current;

    void (async () => {
      try {
        const page = await fetchPageRef.current(query, endCursor);
        if (requestRef.current !== requestId) return;
        remember(page.options);
        pageRef.current = {
          hasNextPage: Boolean(page.hasNextPage),
          endCursor: page.endCursor ?? null,
        };
        setPageOptions((prev) => {
          const seenIds = new Set(prev.map((o) => o.id));
          return [...prev, ...page.options.filter((o) => !seenIds.has(o.id))];
        });
      } catch (error) {
        console.error("Select search page failed:", error);
      } finally {
        // Only release the flag if this append still owns it; a superseded one
        // would otherwise clear it for the append that replaced it.
        if (requestRef.current === requestId) appendingRef.current = false;
      }
    })();
  }, [query, remember, skip]);

  const options = useMemo(() => {
    if (!seed) return pageOptions;
    return pageOptions.some((o) => o.id === seed.id)
      ? pageOptions
      : [seed, ...pageOptions];
  }, [pageOptions, seed]);

  const optionById = useCallback((id: string) => {
    const found = seenRef.current.get(id);
    if (found) return found;
    return seedRef.current?.id === id ? seedRef.current : undefined;
  }, []);

  const reset = useCallback(() => setQuery(""), []);

  return {
    options,
    loading,
    query: resultQuery,
    onInputChange: setQuery,
    onScrollEnd,
    optionById,
    reset,
  };
}

export default useSelectSearch;
